import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import { dashboardApi, assetApi } from '../../services/api'

export interface DashboardState {
  loading: boolean
  error: string | null
  stats: {
    totalAssets: number
    totalValue: number
    dailyGrowth: number
    monthlyGrowth: number
    activeProducts: number
    pendingTasks: number
  }
  recentActivities: Array<{
    id: string
    type: 'asset_added' | 'asset_updated' | 'data_imported' | 'report_generated'
    title: string
    description: string
    timestamp: string
    user: string
  }>
  /** 资产分布概览：来自 /api/dashboard/distribution */
  distribution: Array<{ label: string; percent: number }>
  quickActions: Array<{
    id: string
    title: string
    description: string
    icon: string
    path: string
    color: string
    state?: Record<string, unknown> | null
  }>
}

const initialState: DashboardState = {
  loading: false,
  error: null,
  stats: {
    totalAssets: 0,
    totalValue: 0,
    dailyGrowth: 0,
    monthlyGrowth: 0,
    activeProducts: 0,
    pendingTasks: 0,
  },
  recentActivities: [],
  distribution: [],
  quickActions: [
    {
      id: '1',
      title: '添加资产',
      description: '添加新的资产产品',
      icon: 'plus',
      path: '/assets',
      state: { openAddModal: true },
      color: 'blue',
    },
    // {
    //   id: '2',
    //   title: '导入数据',
    //   description: '批量导入数据文件',
    //   icon: 'upload',
    //   path: '/data',
    //   state: null,
    //   color: 'green',
    // },
    {
      id: '3',
      title: '生成报告',
      description: '生成数据分析报告',
      icon: 'file-text',
      path: '/analytics',
      state: null,
      color: 'purple',
    },
    {
      id: '4',
      title: '设置提醒',
      description: '设置数据监控提醒',
      icon: 'bell',
      path: '/settings',
      state: { tab: 'notifications' },
      color: 'orange',
    },
  ],
}

function normalizeDistribution(raw: unknown): Array<{ label: string; percent: number }> {
  const list = Array.isArray(raw)
    ? raw
    : (raw && typeof raw === 'object' && Array.isArray((raw as any).items))
      ? (raw as any).items
      : (raw && typeof raw === 'object' && Array.isArray((raw as any).list))
        ? (raw as any).list
        : []
  if (!list.length) return []
  const total = list.reduce((sum: number, item: any) => sum + (Number(item.value ?? item.amount ?? item.percent ?? 0) || 0), 0)
  return list.map((item: any) => {
    const label = item.name ?? item.label ?? item.type ?? ''
    const value = Number(item.value ?? item.amount ?? item.percent ?? 0) || 0
    const percent = total > 0 && (item.percent != null || item.ratio != null)
      ? Number(item.percent ?? item.ratio ?? 0)
      : total > 0
        ? Math.round((value / total) * 100)
        : 0
    return { label, percent }
  }).filter((d: { label: string; percent: number }) => d.label)
}

function mapDashboardResponse(
  res: unknown
): { stats: DashboardState['stats']; recentActivities: DashboardState['recentActivities']; distribution: DashboardState['distribution'] } {
  if (res && typeof res === 'object' && 'stats' in res && Array.isArray((res as any).recentActivities)) {
    const r = res as { stats?: Record<string, unknown>; recentActivities: unknown[]; distribution?: unknown }
    const distRaw = r.distribution
    return {
      stats: {
        totalAssets: Number(r.stats?.totalAssets ?? r.stats?.total_assets ?? 0),
        totalValue: Number(r.stats?.totalValue ?? r.stats?.total_value ?? 0),
        dailyGrowth: Number(r.stats?.dailyGrowth ?? r.stats?.daily_growth ?? 0),
        monthlyGrowth: Number(r.stats?.monthlyGrowth ?? r.stats?.monthly_growth ?? 0),
        activeProducts: Number(r.stats?.activeProducts ?? r.stats?.active_products ?? 0),
        pendingTasks: Number(r.stats?.pendingTasks ?? r.stats?.pending_tasks ?? 0),
      },
      recentActivities: (r.recentActivities as any[]).map((a, i) => ({
        id: String(a?.id ?? i + 1),
        type: a?.type ?? 'asset_added',
        title: a?.title ?? '',
        description: a?.description ?? '',
        timestamp: a?.timestamp ?? '',
        user: a?.user ?? '',
      })),
      distribution: normalizeDistribution(distRaw),
    }
  }
  return {
    stats: { ...initialState.stats },
    recentActivities: [],
    distribution: [],
  }
}

/** 从资产产品列表计算统计与分布 */
function computeStatsAndDistributionFromAssets(assets: Array<{ value?: number; status?: string; type?: string; category?: string }>) {
  const totalValue = assets.reduce((sum, a) => sum + (Number(a.value) || 0), 0)
  const totalAssets = assets.length
  const activeProducts = assets.filter(a => a.status === 'active').length
  const byCategory: Record<string, number> = {}
  assets.forEach(a => {
    const label = (a.type || a.category || '其他') as string
    byCategory[label] = (byCategory[label] || 0) + (Number(a.value) || 0)
  })
  const totalByCat = Object.values(byCategory).reduce((s, v) => s + v, 0)
  const distribution = Object.entries(byCategory).map(([label, val]) => ({
    label,
    percent: totalByCat > 0 ? Math.round((val / totalByCat) * 100) : 0,
  })).filter(d => d.label)
  return {
    stats: {
      ...initialState.stats,
      totalValue,
      totalAssets,
      activeProducts,
    },
    distribution: distribution.length ? distribution : [],
  }
}

export const fetchDashboardData = createAsyncThunk(
  'dashboard/fetchData',
  async () => {
    try {
      const [assetsRes, dashboardRes] = await Promise.all([
        assetApi.list({ page: 1, pageSize: 100, filters: { status: 'active' } }).catch(() => null),
        dashboardApi.getStats().catch(() => null),
      ])
      const mapped = mapDashboardResponse(dashboardRes)
      let stats = mapped.stats
      let distribution = mapped.distribution
      const list = (assetsRes as any)?.data?.items ?? (assetsRes as any)?.data ?? (assetsRes as any)?.list ?? Array.isArray(assetsRes) ? assetsRes : []
      if (Array.isArray(list) && list.length > 0) {
        const computed = computeStatsAndDistributionFromAssets(list)
        stats = computed.stats
        distribution = computed.distribution
      }
      return { ...mapped, stats, distribution }
    } catch {
      return { ...mapDashboardResponse(null), distribution: [] }
    }
  }
)

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    updateStats: (state, action: PayloadAction<Partial<DashboardState['stats']>>) => {
      state.stats = { ...state.stats, ...action.payload }
    },
    addActivity: (state, action: PayloadAction<DashboardState['recentActivities'][0]>) => {
      state.recentActivities.unshift(action.payload)
      if (state.recentActivities.length > 10) {
        state.recentActivities.pop()
      }
    },
    clearError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDashboardData.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchDashboardData.fulfilled, (state, action) => {
        state.loading = false
        state.stats = action.payload.stats
        state.recentActivities = action.payload.recentActivities
        if (Array.isArray(action.payload.distribution)) {
          state.distribution = action.payload.distribution
        }
      })
      .addCase(fetchDashboardData.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || '获取数据失败'
      })
  },
})

export const { updateStats, addActivity, clearError } = dashboardSlice.actions
export default dashboardSlice.reducer